import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOMServer from "react-dom/server";
import { useParams, useNavigate } from 'react-router-dom';
import {MagnifyingGlassIcon, PlusCircleIcon} from '@heroicons/react/24/solid';
import { Search, Filter, Plus, AlertTriangle, Wifi, Bluetooth, Eye, MapPin, Fingerprint, Logs, ChevronDown, X, BellRing, ArrowLeft } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid} from 'recharts';
import { Target, Trash2, Save, Signal, Clock} from 'lucide-react';
import './theme.css';
import { color, typeColor, rssiColor } from './tokens';



function pad(n) {
  return n.toString().padStart(2, "0");
}

function formatHourLabel(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:00`;
}

function generateHourlyBucketsWithProtocols(start, end) {
  const buckets = [];
  const current = new Date(start);
  current.setMinutes(0, 0, 0, 0); // normalize to top of the hour

  const endHour = new Date(end);
  endHour.setMinutes(0, 0, 0, 0);

  while (current <= endHour) {
    buckets.push({
      timestamp: new Date(current),
      label: formatHourLabel(current),
      BLE: 0,
      WIFI: 0,
      total: 0,
    });
    current.setHours(current.getHours() + 1);
  }
  return buckets;
}

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}


// Real Map component using Leaflet
const RealMap = ({ mapData, currentTrackingPoint, selectedDevice, mapView, timelineValue, drawMode, circleFilter, onCircleDrawn, onCircleClear, columnHeight }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const currentMarkerRef = useRef(null);
  const circleLayerRef = useRef(null);
  const drawStateRef = useRef({ active: false, startLatLng: null });

  useEffect(() => {
  // Load Leaflet CSS and JS dynamically
  const loadLeaflet = async () => {
    if (window.L) {
      initializeMap();
      return;
    }

    // Load CSS
    const cssLink = document.createElement('link');
    cssLink.rel = 'stylesheet';
    cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    cssLink.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    cssLink.crossOrigin = '';
    document.head.appendChild(cssLink);

    // Load JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
    script.crossOrigin = '';
    script.onload = () => {
      initializeMap();
    };
    document.head.appendChild(script);
  };

  const initializeMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Calculate center and zoom based on data
    let center = [27.7676, -82.6403]; // St. Petersburg, FL default
    let zoom = 10;

    if (mapData.length > 0) {
      const lats = mapData.map(d => parseFloat(d.latitude)).filter(lat => !isNaN(lat));
      const lngs = mapData.map(d => parseFloat(d.longitude)).filter(lng => !isNaN(lng));
      
      if (lats.length > 0 && lngs.length > 0) {
        const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
        const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
        center = [centerLat, centerLng];
        
        // Calculate zoom based on data spread
        const latSpread = Math.max(...lats) - Math.min(...lats);
        const lngSpread = Math.max(...lngs) - Math.min(...lngs);
        const maxSpread = Math.max(latSpread, lngSpread);
        
        if (maxSpread < 0.01) zoom = 16;
        else if (maxSpread < 0.05) zoom = 14;
        else if (maxSpread < 0.1) zoom = 12;
        else if (maxSpread < 0.5) zoom = 10;
        else zoom = 8;
      }
    }

    // Initialize map
    const map = window.L.map(mapRef.current, {
      center: center,
      zoom: zoom,
      zoomControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
      dragging: true,
      touchZoom: true
    });

    // Add tile layers
    const streetLayer = window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 18,
      minZoom: 1
    });

    const satelliteLayer = window.L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      maxZoom: 18,
      minZoom: 1
    });

    const topoLayer = window.L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
      attribution: 'Map data: © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: © <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
      maxZoom: 17,
      minZoom: 1
    });

    const cartoDark = window.L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20
    });

    // Add default layer
    if (mapView === 'satellite') {
      satelliteLayer.addTo(map);
    } else if (mapView === 'terrain') {
      topoLayer.addTo(map);
    } else if (mapView === 'dark') {
      cartoDark.addTo(map);
    } else {
      streetLayer.addTo(map);
    }

    // Add layer control
    const baseLayers = {
      "Street Map": streetLayer,
      "Satellite": satelliteLayer,
      "Terrain": topoLayer,
      "Carto Dark": cartoDark
    };

    window.L.control.layers(baseLayers).addTo(map);

    // Add scale control
    window.L.control.scale().addTo(map);

    mapInstanceRef.current = map;

    // ── Circle draw interaction ───────────────────────────────────────────
    map.on('mousedown', (e) => {
      if (!drawStateRef.current.active) return;
      drawStateRef.current.startLatLng = e.latlng;
      if (circleLayerRef.current) { map.removeLayer(circleLayerRef.current); circleLayerRef.current = null; }
      circleLayerRef.current = window.L.circle(e.latlng, {
        radius: 1,
        color: color.signalCyan,
        weight: 2,
        fillColor: color.signalCyan,
        fillOpacity: 0.08,
        dashArray: '5 5',
      }).addTo(map);
    });

    map.on('mousemove', (e) => {
      if (!drawStateRef.current.active || !drawStateRef.current.startLatLng || !circleLayerRef.current) return;
      const r = drawStateRef.current.startLatLng.distanceTo(e.latlng);
      circleLayerRef.current.setRadius(r);
    });

    map.on('mouseup', (e) => {
      if (!drawStateRef.current.active || !drawStateRef.current.startLatLng) return;
      const r = drawStateRef.current.startLatLng.distanceTo(e.latlng);
      if (circleLayerRef.current) {
        circleLayerRef.current.setStyle({ dashArray: null, fillOpacity: 0.1 });
      }
      if (onCircleDrawn) onCircleDrawn({ center: drawStateRef.current.startLatLng, radius: r });
      drawStateRef.current.startLatLng = null;
    });
    // ─────────────────────────────────────────────────────────────────────

    updateMarkers();
  };

  loadLeaflet();

  return () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
  };
}, []);


  // Sync drawMode into ref and toggle map interaction
  useEffect(() => {
    drawStateRef.current.active = drawMode;
    if (!mapInstanceRef.current) return;
    const map = mapInstanceRef.current;
    if (drawMode) {
      map.dragging.disable();
      map.doubleClickZoom.disable();
      if (mapRef.current) mapRef.current.style.cursor = 'crosshair';
    } else {
      map.dragging.enable();
      map.doubleClickZoom.enable();
      if (mapRef.current) mapRef.current.style.cursor = '';
    }
  }, [drawMode]);

  // When circleFilter is cleared externally, remove the circle layer
  useEffect(() => {
    if (!circleFilter && circleLayerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(circleLayerRef.current);
      circleLayerRef.current = null;
    }
  }, [circleFilter]);

  // Update map tiles when view changes
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return;

    const map = mapInstanceRef.current;
    
    // Remove all layers
    map.eachLayer(layer => {
      if (layer._url) map.removeLayer(layer);
    });

    // Add appropriate layer
    let newLayer;
    if (mapView === 'satellite') {
      newLayer = window.L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        maxZoom: 18
      });
    } else if (mapView === 'terrain') {
      newLayer = window.L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
        attribution: 'Map data: © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: © <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
        maxZoom: 17
      });
    } else if (mapView === 'dark') {
      newLayer = window.L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20
      });
    } else {
      newLayer = window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 18
      });
    }

    newLayer.addTo(map);
  }, [mapView]);

  // The map's height is now flexible (fills whatever space its column has), so
  // Leaflet needs an explicit nudge to redraw tiles whenever that space changes.
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const id = setTimeout(() => mapInstanceRef.current.invalidateSize(), 0);
    return () => clearTimeout(id);
  }, [columnHeight]);

  // Update markers when data changes
  const updateMarkers = () => {
    if (!mapInstanceRef.current || !window.L) return;

    const map = mapInstanceRef.current;

    // Clear existing markers
    markersRef.current.forEach(marker => map.removeLayer(marker));
    markersRef.current = [];
    if (currentMarkerRef.current) {
      map.removeLayer(currentMarkerRef.current);
      currentMarkerRef.current = null;
    }

    const groupedPoints = {};

    mapData.forEach(point => {
      const lat = parseFloat(point.latitude);
      const lng = parseFloat(point.longitude);
      if (isNaN(lat) || isNaN(lng)) return;

      const key = `${lat.toFixed(6)}_${lng.toFixed(6)}`;
      if (!groupedPoints[key]) groupedPoints[key] = [];
      groupedPoints[key].push(point);
    });

    // Add new markers for each data point
    Object.values(groupedPoints).forEach(pointsAtLocation => {
      const lat = parseFloat(pointsAtLocation[0].latitude);
      const lng = parseFloat(pointsAtLocation[0].longitude);
      const count = pointsAtLocation.length;

      // Create a simple div showing the number of devices
      const markerHtml = `
        <div style="
          background-color: ${color.signalCyan};
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 3px solid white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: white;
        ">
          ${count}
        </div>
      `;

      const icon = window.L.divIcon({
        html: markerHtml,
        className: "device-count-marker",
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });

      const marker = window.L.marker([lat, lng], { icon });
      
      marker.addTo(map);
      markersRef.current.push(marker);
    });

    // Add current tracking point marker
    if (currentTrackingPoint) {
      const lat = parseFloat(currentTrackingPoint.latitude);
      const lng = parseFloat(currentTrackingPoint.longitude);
      
      if (!isNaN(lat) && !isNaN(lng)) {

        const protocolKey = currentTrackingPoint.protocol === "BLE" ? 'ble' : currentTrackingPoint.protocol === "WIFI" ? 'wifi' : null;
        const IconComponent = protocolKey === 'ble' ? Bluetooth : Wifi;
        const markerColor = typeColor(protocolKey);

        // React component for marker
        const MarkerWithIcon = () => (
          <div
            style={{
              backgroundColor: markerColor,
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              border: "3px solid white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "16px",
              color: "white",
              animation: "pulse 2s infinite",
            }}
          >
            <IconComponent size={16} />
            <style>
              {`
                @keyframes pulse {
                  0% { transform: scale(1); opacity: 1; }
                  50% { transform: scale(1.1); opacity: 1; }
                  100% { transform: scale(1); opacity: 1; }
                }
              `}
            </style>
          </div>
        );

        // Render to string for Leaflet
        const currentMarkerHtml = ReactDOMServer.renderToString(<MarkerWithIcon />);

        const currentIcon = window.L.divIcon({
          html: currentMarkerHtml,
          className: "current-device-marker",
          iconSize: [30, 30],
          iconAnchor: [15, 15],
        });

        currentMarkerRef.current = window.L.marker([lat, lng], { icon: currentIcon });
        currentMarkerRef.current.addTo(map);

        // Center map on current point
        map.panTo([lat, lng]);
      }
    }
  };

  useEffect(() => {
    updateMarkers();
  }, [mapData, currentTrackingPoint, selectedDevice, timelineValue]);

  return (
    <div className="relative w-full h-full">
      <div ref={mapRef} className="w-full h-full rounded-lg" />
      {mapData.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-[var(--surface-900)] bg-opacity-75 rounded-lg">
          <div className="text-center text-[var(--text-muted)]">
            <div className="text-lg font-medium mb-2">No location data available</div>
            <div className="text-sm">
              {selectedDevice ? 'This device has no location data' : 'No devices have location data'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tree Dropdown for nested field selection ──────────────────────────────────
//
// Builds a tree from availableFields whose `value` may be a dot-path like
// "manufacturer_data.tags.company".  Each intermediate segment that has
// children shows a right-arrow and expands on click; leaf nodes select the
// full dot-path and close the panel.
//
function buildFieldTree(fields) {
  // tree node: { label, value (full path, leaves only), children: {} }
  const root = {};
  fields.forEach(({ value, label }) => {
    const parts = value.split('.');
    let node = root;
    parts.forEach((part, i) => {
      if (!node[part]) node[part] = { _children: {}, _leaf: null };
      if (i === parts.length - 1) {
        // leaf – store the full path + display label
        node[part]._leaf = { value, label };
      }
      node = node[part]._children;
    });
  });
  return root;
}

function FieldTreeNode({ nodeKey, node, depth, selectedValue, onSelect, expandedMap, setExpandedMap }) {
  const pathKey   = `tree_${depth}_${nodeKey}`;
  const hasKids   = Object.keys(node._children).length > 0;
  const isExpanded = !!expandedMap[pathKey];
  const isSelected = node._leaf && node._leaf.value === selectedValue;
  const [hovered, setHovered] = useState(false);

  const toggle = (e) => {
    e.stopPropagation();
    setExpandedMap(prev => ({ ...prev, [pathKey]: !prev[pathKey] }));
  };

  const select = (e) => {
    e.stopPropagation();
    onSelect(node._leaf.value);
  };

  const bg = isSelected || hovered ? 'var(--surface-800)' : 'transparent';
  const textColor = isSelected ? 'var(--signal-cyan)' : depth === 0 ? 'var(--text-primary)' : 'var(--text-muted)';

  return (
    <>
      <div
        onClick={hasKids ? toggle : (node._leaf ? select : undefined)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: `8px 12px 8px ${12 + depth * 16}px`,
          cursor: hasKids || node._leaf ? 'pointer' : 'default',
          background: bg, color: textColor,
          transition: 'background .15s, color .15s',
          borderBottom: '1px solid var(--ink-950)',
          userSelect: 'none',
          borderLeft: depth > 0 ? `2px solid ${isSelected ? 'var(--signal-cyan)' : 'var(--surface-800)'}` : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {hasKids && (
            <span style={{
              fontSize: 9, color: 'var(--text-muted)',
              background: 'var(--ink-950)', border: '1px solid var(--surface-800)',
              padding: '1px 5px', borderRadius: 3, letterSpacing: 1,
              fontFamily: 'var(--font-mono)',
            }}>obj</span>
          )}
          <span style={{ fontSize: 13 }}>
            {node._leaf?.label ?? nodeKey}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {hasKids && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke={isExpanded || hovered ? 'var(--signal-cyan)' : 'var(--text-muted)'} strokeWidth="2"
              style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}>
              <path d="M9 18l6-6-6-6" />
            </svg>
          )}
          {isSelected && (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--signal-cyan)" strokeWidth="3">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          )}
        </div>
      </div>

      {hasKids && isExpanded && Object.entries(node._children).map(([childKey, childNode]) => (
        <FieldTreeNode
          key={childKey}
          nodeKey={childKey}
          node={childNode}
          depth={depth + 1}
          selectedValue={selectedValue}
          onSelect={onSelect}
          expandedMap={expandedMap}
          setExpandedMap={setExpandedMap}
        />
      ))}
    </>
  );
}

function FieldTreeDropdown({ availableFields, selectedValue, onChange }) {
  const [open, setOpen]               = useState(false);
  const [expandedMap, setExpandedMap] = useState({});
  const ref                           = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const tree = useMemo(() => buildFieldTree(availableFields), [availableFields]);

  const selectedLabel = useMemo(() => {
    const found = availableFields.find(f => f.value === selectedValue);
    return found ? found.label : null;
  }, [availableFields, selectedValue]);

  const handleSelect = (value) => { onChange(value); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md px-3 py-2 text-sm text-[var(--text-muted)] flex items-center gap-2"
        style={{
          minWidth: 160, cursor: 'pointer', outline: 'none',
          borderColor: selectedValue ? 'var(--signal-cyan)' : 'var(--surface-800)',
          color: selectedValue ? 'var(--text-primary)' : 'var(--text-muted)',
          background: selectedValue ? 'var(--surface-800)' : 'var(--ink-950)',
        }}
      >
        <Filter size={14} style={{ flexShrink: 0, color: selectedValue ? 'var(--signal-cyan)' : 'var(--text-muted)' }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
          {selectedLabel || 'Select Key'}
        </span>
        <ChevronDown size={14} style={{
          flexShrink: 0, color: 'var(--text-muted)',
          transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s'
        }} />
      </button>

      {open && availableFields.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 9999,
          background: 'var(--surface-900)', border: '1px solid var(--surface-800)', borderRadius: 6,
          minWidth: 240, maxHeight: 360, overflowY: 'auto',
          boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
        }}>
          {Object.entries(tree).map(([key, node]) => (
            <FieldTreeNode
              key={key}
              nodeKey={key}
              node={node}
              depth={0}
              selectedValue={selectedValue}
              onSelect={handleSelect}
              expandedMap={expandedMap}
              setExpandedMap={setExpandedMap}
            />
          ))}
        </div>
      )}
    </div>
  );
}
// ── End Tree Dropdown ─────────────────────────────────────────────────────────

// Keys assessed for the completeness score.
// Each entry: { path: dot-notation string used by getNestedValue, label: human name }
const COMPLETENESS_KEYS = [
  { path: 'mac_address',        label: 'MAC Address' },
  { path: 'mac_vendor',         label: 'MAC Vendor' },
  { path: 'device_name',        label: 'Device Name' },
  { path: 'wifi_ssid',          label: 'WIFI SSID' },
  { path: 'ble_company_name',   label: 'BLE Company' },
  { path: 'ble_device_model',   label: 'BLE Device Model' },
  { path: 'ble_device_color',   label: 'BLE Device Color' },
  { path: 'rssi',               label: 'RSSI' },
  { path: 'sensor_name',        label: 'Sensor Name' },
  { path: 'latitude',           label: 'Latitude' },
  { path: 'longitude',          label: 'Longitude' },
  { path: 'timestamp',          label: 'Timestamp' },
  { path: 'protocol',           label: 'Protocol' },
  { path: 'address_type',       label: 'Address Type' },
];

// Computes completeness: across all matching logs, what fraction of the
// COMPLETENESS_KEYS have at least one non-null/empty value.
function computeCompleteness(matches) {
  if (!matches || matches.length === 0) return { score: 0, present: [], missing: [] };
  const present = [];
  const missing = [];
  COMPLETENESS_KEYS.forEach(({ path, label }) => {
    const hasValue = matches.some(item => {
      const val = getNestedValue(item, path);
      return val !== null && val !== undefined && val !== '';
    });
    if (hasValue) present.push(label);
    else missing.push(label);
  });
  const score = Math.round((present.length / COMPLETENESS_KEYS.length) * 100);
  return { score, present, missing };
}

function CompletenessIndicator({ matches }) {
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef(null);
  const btnRef = useRef(null);

  const { score, present, missing } = useMemo(() => computeCompleteness(matches), [matches]);

  // Close popover on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setShowPopover(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const scoreColor = score >= 75 ? 'var(--status-success)' : score >= 40 ? 'var(--status-warn)' : 'var(--status-error)';
  const trackColor = score >= 75
    ? 'color-mix(in srgb, var(--status-success) 15%, transparent)'
    : score >= 40
    ? 'color-mix(in srgb, var(--status-warn) 15%, transparent)'
    : 'color-mix(in srgb, var(--status-error) 15%, transparent)';

  return (
    <div className="bg-[var(--surface-900)] border border-[var(--surface-800)] rounded-lg p-3 text-center relative">
      {/* Score */}
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <div className="text-xl font-bold" style={{ color: scoreColor }}>{score}%</div>
        <button
          ref={btnRef}
          onClick={() => setShowPopover(v => !v)}
          className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors flex-shrink-0"
          title="What is completeness?"
        >
          {/* Simple inline info circle — no extra import needed */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="8" strokeWidth="3" />
            <line x1="12" y1="12" x2="12" y2="16" />
          </svg>
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ background: trackColor, borderRadius: 4, height: 4, margin: '4px 0 6px' }}>
        <div style={{ background: scoreColor, borderRadius: 4, height: 4, width: `${score}%`, transition: 'width .4s ease' }} />
      </div>

      <div className="text-xs text-[var(--text-muted)]">Completeness</div>

      {/* Popover */}
      {showPopover && (
        <div
          ref={popoverRef}
          className="absolute z-50 right-0 mt-2 w-72 bg-[var(--surface-900)] border border-[var(--surface-800)] rounded-lg shadow-2xl p-4 text-left"
          style={{ top: '100%' }}
        >
          <div className="flex items-start justify-between mb-2">
            <span className="text-sm font-semibold text-[var(--text-primary)]">Completeness Score</span>
            <button onClick={() => setShowPopover(false)} aria-label="Close" className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
              <X size={14} />
            </button>
          </div>
          <p className="text-xs text-[var(--text-muted)] mb-3">
            Completeness shows how many of the following fields have at least one non-empty value across all logs matched by this profile.
          </p>

          {/* Present keys */}
          {present.length > 0 && (
            <div className="mb-2">
              <div className="text-xs font-medium text-[var(--status-success)] mb-1">Present ({present.length})</div>
              <div className="space-y-0.5">
                {present.map(label => (
                  <div key={label} className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--status-success)" strokeWidth="3">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    {label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Missing keys */}
          {missing.length > 0 && (
            <div>
              <div className="text-xs font-medium text-[var(--status-error)] mb-1">Missing ({missing.length})</div>
              <div className="space-y-0.5">
                {missing.map(label => (
                  <div key={label} className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--status-error)" strokeWidth="3">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                    {label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── End Completeness ──────────────────────────────────────────────────────────

const MissionView = ({ token }) => {
  const { id:missionId } = useParams();
  const navigate = useNavigate();
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [activeView, setActiveView] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [missionData, setMissionData] = useState([]);
  const [missionInfo, setMissionInfo] = useState([]);
  const [missionTags, setMissionTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKey, setSelectedKey] = useState('');
  const [selectedValue, setSelectedValue] = useState('');

  const [viewMode, setViewMode] = useState("stats"); //  "stats" | "map"

  const [wifiSortKey, setWifiSortKey] = useState("timestamp");
  const [wifiSortAsc, setWifiSortAsc] = useState(true);

  const [bleSortKey, setBleSortKey] = useState("timestamp");
  const [bleSortAsc, setBleSortAsc] = useState(true);

  const [logsView, setLogsView] = useState("wifi"); // "wifi" | "ble" — which protocol's log table is showing

  const [profiles, setProfiles] = useState([]);
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [showCreateProfile, setShowCreateProfile] = useState(false);

  const [availableFields, setAvailableFields] = useState([]);

  // const availableFields = [
  //   { value: 'ble_company_name', label: 'BLE Company', protocols: ['ble'], icon: Bluetooth },
  //   { value: 'device_name', label: 'Device Name', protocols: ['ble'], icon: Bluetooth },
  //   { value: 'mac_address', label: 'MAC Address', protocols: ['wifi', 'ble'], icon: Target },
  //   { value: 'mac_vendor', label: 'MAC Vendor', protocols: ['wifi', 'ble'], icon: Target },
  //   { value: 'manufacturer_data', label: 'Manufacturer Data', protocols: ['ble'], icon: Bluetooth },
  //   { value: 'rssi', label: 'RSSI', protocols: ['wifi', 'ble'], icon: Signal },
  //   { value: 'sensor_name', label: 'Sensor Name', protocols: ['wifi', 'ble'], icon: MapPin },
  //   { value: 'wifi_ssid', label: 'WIFI SSID', protocols: ['wifi'], icon: Wifi }
  // ];

  const operators = [
    { value: 'is', label: 'equals', symbol: '=' },
    { value: 'is_not', label: 'does not equal', symbol: '≠' },
    { value: 'contains', label: 'contains', symbol: '⊇' },
    { value: 'not_contains', label: 'does not contain', symbol: '⊅' },
    { value: 'is_null', label: 'is empty', symbol: '∅' },
    { value: 'not_null', label: 'is not empty', symbol: '∃' },
    { value: 'greater_than', label: 'greater than', symbol: '>' },
    { value: 'less_than', label: 'less than', symbol: '<' }
  ];



  const [alerts, setAlerts] = useState([]);
  const [showNewAlertModal, setShowNewAlertModal] = useState(false);
  const [newAlert, setNewAlert] = useState({
    name: '',
    field: '',
    operator: 'equals',
    value: '',
    active: true
  });
  const [selectedDevice, setSelectedDevice] = useState('');
  const [timelineValue, setTimelineValue] = useState(0);
  const [profileTimelineValue, setprofileTimelineValue] = useState(0);

  const [mapView, setMapView] = useState('dark');
  const [macSearch, setMacSearch] = useState('');

  // Measures the MAC list panel so the map/timeline/detail column on the
  // right can be capped to the same height instead of overflowing past it.
  const macListPanelRef = useRef(null);
  const [mapColumnHeight, setMapColumnHeight] = useState(null);

  useEffect(() => {
    const el = macListPanelRef.current;
    if (!el) {
      setMapColumnHeight(null);
      return;
    }
    const update = () => setMapColumnHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [activeView]);

  const iconMap = {
    bluetooth: Bluetooth,
    wifi: Wifi,
    signal: Signal,
    target: Target,
    "map-pin": MapPin,
  };

  // Fetch mission data
  useEffect(() => {
    const fetchMissionData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/mission/data/${missionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch mission data');
        
        const data = await response.json();
        setMissionData(data['mission_data']);
        setMissionInfo(data['mission_info']);
        setAvailableFields(
          data.fields
            .map(field => ({
              ...field,
              icon: iconMap[field.icon] || Target
            }))
            .sort((a, b) =>
              a.label.toLowerCase().localeCompare(b.label.toLowerCase())
            )
        );

        // let uniqueTags = new Set();
        // data.forEach(packet => {
        //   let tags = packet?.manufacturer_data?.tags;
        //   if (tags) {
        //     Object.keys(tags).forEach(tag => uniqueTags.add(tag));
        //   }
        // });
        // setMissionTags(uniqueTags);
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMissionData();
  }, [missionId, token]);

  const distinctValues = selectedKey
  ? Array.from(
      new Set(
        missionData
          .map((item) => getNestedValue(item, selectedKey))
          .filter((val) => val !== null && val !== undefined && val !== '')
      )
    ).sort((a, b) => String(a).localeCompare(String(b)))
  : [];

  // Separate WIFI and BLE data
  const wifiData = useMemo(() => {
    return missionData.filter(item => item.protocol === 'WIFI' || item.wifi_ssid !== null);
  }, [missionData]);

  const bleData = useMemo(() => {
    return missionData.filter(item => item.protocol === 'BLE' || item.device_name !== null);
  }, [missionData]);

  // Filter data based on search and filters
  const filteredWifiData = useMemo(() => {
    return wifiData.filter(item => {
      const searchMatch = !searchQuery || 
        Object.values(item).some(val => 
          val?.toString().toLowerCase().includes(searchQuery.toLowerCase())
        );
      // console.log(activeFilters);
      const filterMatch = Object.entries(activeFilters).every(([field, value]) => {
        if (!value) return true;
        const fieldValue = getNestedValue(item, field);

        return fieldValue
          ?.toString()
          .toLowerCase()
          .includes(value.toLowerCase());
      });
      
      return searchMatch && filterMatch;
    });
  }, [wifiData, searchQuery, activeFilters]);

  const filteredBleData = useMemo(() => {
    return bleData.filter(item => {
      const searchMatch = !searchQuery || 
        Object.values(item).some(val => 
          val?.toString().toLowerCase().includes(searchQuery.toLowerCase())
        );
      
      const filterMatch = Object.entries(activeFilters).every(([field, value]) => {
        if (!value) return true;
        const fieldValue = getNestedValue(item, field);

        return fieldValue
          ?.toString()
          .toLowerCase()
          .includes(value.toLowerCase());
      });
      
      return searchMatch && filterMatch;
    });
  }, [bleData, searchQuery, activeFilters]);

  const matchesRule = (device, rule) => {
  const fieldValue = getNestedValue(device, rule.field);
  const ruleValue = rule.value;

  switch (rule.operator) {
    case 'is':
      return fieldValue === ruleValue;
    case 'is_not':
      return fieldValue !== ruleValue;
    case 'contains':
      return fieldValue && fieldValue.toString().toLowerCase().includes(ruleValue.toLowerCase());
    case 'not_contains':
      return !fieldValue || !fieldValue.toString().toLowerCase().includes(ruleValue.toLowerCase());
    case 'is_null':
      return fieldValue === null || fieldValue === undefined || fieldValue === '';
    case 'not_null':
      return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
    case 'greater_than':
      return parseFloat(fieldValue) > parseFloat(ruleValue);
    case 'less_than':
      return parseFloat(fieldValue) < parseFloat(ruleValue);
    default:
      return false;
  }
};

// Function to get all matching packets for a profile (OR logic + MAC grouping)
const getProfileMatches = (profile) => {
  const allData = [...filteredWifiData, ...filteredBleData];
  
  // Find all devices that match ANY rule (OR logic)
  const directMatches = allData.filter(device => 
    profile.rules.some(rule => matchesRule(device, rule))
  );

  // Get all MAC addresses from direct matches
  const matchedMacAddresses = [...new Set(directMatches.map(device => device.mac_address))];

  // Find ALL packets with those MAC addresses
  const allMatchingPackets = allData.filter(device => 
    matchedMacAddresses.includes(device.mac_address)
  );

  return allMatchingPackets;
};

// Create Profile Modal Component
const CreateProfileModal = () => {
  const [profileName, setProfileName] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [rules, setRules] = useState([{ field: '', operator: '', value: '' }]);

  const addRule = () => {
    setRules([...rules, { field: '', operator: '', value: '' }]);
  };

  const removeRule = (index) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const updateRule = (index, key, value) => {
    const newRules = [...rules];
    newRules[index][key] = value;
    setRules(newRules);
  };

  const needsValue = (operator) => {
    return !['is_null', 'not_null'].includes(operator);
  };

  const createProfile = () => {
    if (!profileName || rules.some(rule => !rule.field || !rule.operator || (needsValue(rule.operator) && !rule.value))) {
      alert('Please fill in all required fields');
      return;
    }

    const newProfile = {
      id: Date.now(),
      name: profileName,
      description: profileDescription,
      rules: rules.filter(rule => rule.field && rule.operator),
      created: new Date().toISOString()
    };

    setProfiles([...profiles, newProfile]);
    setShowCreateProfile(false);
    setProfileName('');
    setProfileDescription('');
    setRules([{ field: '', operator: '', value: '' }]);
  };

  if (!showCreateProfile) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[var(--surface-900)] border border-[var(--surface-800)] rounded-xl p-6 w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <h3 className="text-xl font-semibold text-[var(--text-primary)]">Create Target Profile</h3>
          </div>
          <button
            onClick={() => setShowCreateProfile(false)}
            aria-label="Close"
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6">
          <div className="col-span-2 space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                Profile Name *
              </label>
              <input
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--signal-cyan)] focus:border-transparent transition-all"
                placeholder="Enter profile name (e.g., 'Apple Ecosystem', 'Corporate Devices')"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                Description
              </label>
              <textarea
                value={profileDescription}
                onChange={(e) => setProfileDescription(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--signal-cyan)] focus:border-transparent transition-all resize-none"
                rows={2}
                placeholder="Describe the purpose of this profile..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                Filters (Any packet matching ANY filter gets included - OR logic)
              </label>
              <div className="space-y-3">
                {rules.map((rule, index) => {
                  const fieldObj = availableFields.find(f => f.value === rule.field);
                  const operatorObj = operators.find(o => o.value === rule.operator);
                  const FieldIcon = fieldObj?.icon || Target;
                  
                  return (
                    <div key={index} className="flex items-center space-x-3 p-4 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg">
                      <div className="flex items-center space-x-2 text-[var(--text-muted)]">
                        <FieldIcon size={16} />
                        <span className="text-xs">#{index + 1}</span>
                      </div>
                      
                      <div className="flex-1">
                        <FieldTreeDropdown
                          availableFields={availableFields}
                          selectedValue={rule.field}
                          onChange={(value) => updateRule(index, 'field', value)}
                        />
                      </div>

                      <div className="flex items-center px-3 py-2 bg-[var(--surface-800)] rounded text-[var(--text-primary)] min-w-[60px] justify-center">
                        {operatorObj?.symbol || '?'}
                      </div>

                      <div className="relative flex-1">
                        <select
                          value={rule.operator}
                          onChange={(e) => updateRule(index, 'operator', e.target.value)}
                          className="w-full appearance-none pl-3 pr-9 py-2 bg-[var(--surface-900)] border border-[var(--surface-800)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--signal-cyan)] focus:border-[var(--signal-cyan)] transition-colors"
                        >
                          <option value="">Select Operator</option>
                          {operators.map(op => (
                            <option key={op.value} value={op.value}>
                              {op.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                      </div>

                      {needsValue(rule.operator) && (
                        <input
                          type="text"
                          value={rule.value}
                          onChange={(e) => updateRule(index, 'value', e.target.value)}
                          className="flex-1 px-3 py-2 bg-[var(--surface-900)] border border-[var(--surface-800)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--signal-cyan)] transition-all"
                          placeholder="Enter value"
                        />
                      )}

                      <button
                        onClick={() => removeRule(index)}
                        className="text-[var(--status-error)] hover:text-[var(--text-primary)] transition-colors p-1"
                        disabled={rules.length === 1}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={addRule}
                className="mt-3 flex items-center space-x-2 text-[var(--signal-cyan)] hover:text-[var(--text-primary)] transition-colors"
              >
                <Plus size={16} />
                <span>Add Another Filter</span>
              </button>
            </div>
          </div>

          <div className="space-y-4 col-span-2">
            <div className="p-4 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg">
              <h4 className="text-sm font-medium text-[var(--text-primary)] mb-2">How it works:</h4>
              <div className="text-xs text-[var(--text-muted)] space-y-2">
                <div>• Any packet matching ANY filter gets included</div>
                <div>• All packets with the same MAC address are automatically grouped together</div>
                <div>• Creates a profile across WIFI and BLE protocols</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-[var(--surface-800)] mt-6">          
          <button
                onClick={() => setShowCreateProfile(false)}
                className="mt-4 text-[var(--text-muted)] border border-[var(--surface-800)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
              >
                Cancel
          </button>

          <button
                onClick={createProfile}
                className="mt-4 text-[var(--status-success)] border border-[var(--status-success)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
              >
                Submit
          </button>
        </div>
      </div>
    </div>
  );
};


  const hourlyLogsByProtocol = useMemo(() => {
      if (!missionInfo?.start_time || !missionInfo?.end_time) return [];

      const start = new Date(missionInfo.start_time);
      const end = new Date(missionInfo.end_time);
      if (isNaN(start) || isNaN(end)) return [];

      const buckets = generateHourlyBucketsWithProtocols(start, end);
      const bucketMap = new Map(buckets.map((b) => [b.timestamp.getTime(), b]));

      [...filteredWifiData, ...filteredBleData].forEach((item) => {
        if (!item?.timestamp) return;
        const ts = new Date(item.timestamp);
        ts.setMinutes(0, 0, 0, 0);
        const key = ts.getTime();
        if (!bucketMap.has(key)) return; // outside mission range

        const bucket = bucketMap.get(key);
        if (item.protocol === "BLE") {
          bucket.BLE++;
        } else if (item.protocol === "WIFI") {
          bucket.WIFI++;
        } else {
          // fallback if protocol missing
          if (item.wifi_ssid) bucket.WIFI++;
          else bucket.BLE++;
        }
        bucket.total = bucket.BLE + bucket.WIFI;
      });

      return Array.from(bucketMap.values()).sort(
        (a, b) => a.timestamp - b.timestamp
      );
    }, [missionInfo, filteredWifiData, filteredBleData]);



  // Get unique sensors
  const sensors = useMemo(() => {
    const sensorMap = new Map();
    missionData.forEach(item => {
      if (!sensorMap.has(item.sensor_id)) {
        sensorMap.set(item.sensor_id, {
          id: item.sensor_id,
          name: item.name || `Sensor ${item.sensor_id.slice(-4)}`,
          status: 'active'
        });
      }
    });
    return Array.from(sensorMap.values());
  }, [missionData]);

  // Get device tracking data
  const deviceTrackingData = useMemo(() => {
    const trackingMap = {};
    
    missionData.forEach(item => {
      const deviceId = item.mac_address;
      if (!deviceId) return;
      
      if (!trackingMap[deviceId]) {
        trackingMap[deviceId] = [];
      }
      
      trackingMap[deviceId].push({
        timestamp: item.timestamp,
        sensor_id: item.sensor_id,
        sensor_name: item.sensor_name,
        rssi: item.rssi,
        latitude: item.latitude,
        longitude: item.longitude,
        device_name: item.device_name,
        mac_address: item.mac_address,
        protocol: item.protocol,
        wifi_ssid: item.wifi_ssid
      });
    });
    
    // Sort tracking data by timestamp
    Object.keys(trackingMap).forEach(deviceId => {
      trackingMap[deviceId].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    });
    
    return trackingMap;
  }, [missionData]);

  // Get unique devices for selection
  // Get unique MAC addresses from filtered data
  const { uniqueDevices, vendorData, companyData } = useMemo(() => {
    const devices = new Set();
    const mac_vendor_counts = {};
    const ble_company_counts = {};

    // Combine WIFI + BLE data, just like in triggeredDevices
    [...filteredWifiData, ...filteredBleData].forEach(device => {
      if (device.mac_address) {
        devices.add(device.mac_address);
      }
      if (device.mac_vendor)
        mac_vendor_counts[device.mac_vendor] = (mac_vendor_counts[device.mac_vendor] || 0) + 1;

       if (device.ble_company_name)
        ble_company_counts[device.ble_company_name] = (ble_company_counts[device.ble_company_name] || 0) + 1;
    });

    return {
      uniqueDevices: Array.from(devices),
      vendorData: Object.entries(mac_vendor_counts).map(([vendor, count]) => ({ vendor, count })),
      companyData: Object.entries(ble_company_counts).map(([company, count]) => ({ company, count }))
    };
  }, [filteredWifiData, filteredBleData]);

  const uniqueBLEDevices = useMemo(() => {
    const devices = new Set();
    [...filteredBleData].forEach(device => {
      if (device.mac_address) {
        devices.add(device.mac_address);
      }
    });

    return Array.from(devices);
  }, [filteredBleData]);

  const uniqueWIFIDevices = useMemo(() => {
    const devices = new Set();
    [...filteredWifiData].forEach(device => {
      if (device.mac_address) {
        devices.add(device.mac_address);
      }
    });

    return Array.from(devices);
  }, [filteredWifiData]);

  // --- sort logic  ---
  const sortData = useCallback((data, key, asc) => {
    return [...data].sort((a, b) => {
      const valA = a[key];
      const valB = b[key];

      if (valA == null) return 1;
      if (valB == null) return -1;

      // Optimized string comparison with numeric and case-insensitive options
      if (typeof valA === "string") {
        const comparison = valA.localeCompare(valB, undefined, { 
          numeric: true, 
          sensitivity: 'base' 
        });
        return asc ? comparison : -comparison;
      }
      return asc ? valA - valB : valB - valA;
    });
  }, []);

  const handleWifiSort = (key) => {
    if (wifiSortKey === key) setWifiSortAsc(!wifiSortAsc);
    else {
      setWifiSortKey(key);
      setWifiSortAsc(true);
    }
  };

  const handleBleSort = (key) => {
    if (bleSortKey === key) setBleSortAsc(!bleSortAsc);
    else {
      setBleSortKey(key);
      setBleSortAsc(true);
    }
  };

  const sortedWifiData = useMemo(() => 
    sortData(filteredWifiData, wifiSortKey, wifiSortAsc),
    [filteredWifiData, wifiSortKey, wifiSortAsc, sortData]
  );
  
  const sortedBleData = useMemo(() => 
    sortData(filteredBleData, bleSortKey, bleSortAsc),
    [filteredBleData, bleSortKey, bleSortAsc, sortData]
  );

  // --- end sort logic  ---

  // Get current device tracking points
  const currentDeviceData = deviceTrackingData[selectedDevice] || [];
  const currentTrackingPoint = currentDeviceData[timelineValue] || null;

  // Check for triggered alerts
  const triggeredDevices = useMemo(() => {
    const triggered = [];
    alerts.filter(alert => alert.active).forEach(alert => {
      [...filteredWifiData, ...filteredBleData].forEach(device => {
        const fieldValue = device[alert.field];
        let isTriggered = false;
        
        switch (alert.operator) {
          case 'equals':
            isTriggered = fieldValue === alert.value;
            break;
          case 'contains':
            isTriggered = fieldValue?.toString().toLowerCase().includes(alert.value.toLowerCase());
            break;
          case 'greater_than':
            isTriggered = Number(fieldValue) > Number(alert.value);
            break;
          case 'less_than':
            isTriggered = Number(fieldValue) < Number(alert.value);
            break;
        }
        
        if (isTriggered) {
          triggered.push({ ...device, alertId: alert.id, alertName: alert.name });
        }
      });
    });
    return triggered;
  }, [filteredWifiData, filteredBleData, alerts]);

  // Alert functions
  const addAlert = () => {
    if (newAlert.name && newAlert.field && newAlert.value) {
      setAlerts([...alerts, {
        ...newAlert,
        id: `A${(alerts.length + 1).toString().padStart(3, '0')}`,
        triggeredCount: 0
      }]);
      setNewAlert({ name: '', field: '', operator: 'equals', value: '', active: true });
      setShowNewAlertModal(false);
    }
  };

  const removeAlert = (alertId) => {
    setAlerts(alerts.filter(a => a.id !== alertId));
  };

  const toggleAlert = (alertId) => {
    setAlerts(alerts.map(a => a.id === alertId ? {...a, active: !a.active} : a));
  };

  const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const { name, value, fill } = payload[0];

  return (
    <div
      className="custom-tooltip"
      style={{
        backgroundColor: color.surface800,
        border: `1px solid ${color.surface900}`,
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
        fontSize: '14px',
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      {/* Label with colored dot */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ color: color.textPrimary, fontSize: '12px', fontWeight: 'bold' }}>{`${name}: ${value}`}</span>
      </div>

      {/* Optional description */}
      <p style={{ color: color.textMuted, fontSize: '12px', margin: 0 }}>{`${((value / companyData.reduce((sum, d) => sum + d.count, 0)) * 100).toFixed(1)}% of filtered logs`}</p>
    </div>
  );
};


  const renderOverview = () => (
    <div className="flex flex-col flex-1 space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6">

        <div className="p-4 bg-[var(--surface-900)] rounded-xl border border-[var(--surface-800)]">
          <div className="flex items-center gap-3">
            <Logs className="w-6 h-6 text-emerald-400" />
            <span className="text-lg font-semibold text-emerald-400">Log Count</span>
          </div>
          <p className="mt-2 text-3xl font-bold text-[var(--text-primary)]">{filteredBleData.length + filteredWifiData.length}</p>
        </div>

        <div className="p-4 bg-[var(--surface-900)] rounded-xl border border-[var(--surface-800)]">
          <div className="flex items-center gap-3">
            <Wifi className="w-6 h-6 text-[var(--data-wifi)]" />
            <span className="text-lg font-semibold text-[var(--data-wifi)]">WIFI MAC Addresses</span>
          </div>
          <p className="mt-2 text-3xl font-bold text-[var(--text-primary)]">{uniqueWIFIDevices.length}</p>
        </div>

        <div className="p-4 bg-[var(--surface-900)] rounded-xl border border-[var(--surface-800)]">
          <div className="flex items-center gap-3">
            <Bluetooth className="w-6 h-6 text-[var(--data-ble)]" />
            <span className="text-lg font-semibold text-[var(--data-ble)]">BLE MAC Addresses</span>
          </div>
          <p className="mt-2 text-3xl font-bold text-[var(--text-primary)]">{uniqueBLEDevices.length}</p>
        </div>

        <div className="p-4 bg-[var(--surface-900)] rounded-xl border border-[var(--surface-800)]">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-400" />
            <span className="text-lg font-semibold text-amber-400">Enabled Rules</span>
          </div>
          <p className="mt-2 text-3xl font-bold text-[var(--text-primary)]">{alerts.filter(a => a.active).length}</p>
        </div>
        
        <div className="p-4 bg-[var(--surface-900)] rounded-xl border border-[var(--surface-800)]">
          <div className="flex items-center gap-3">
            <BellRing className="w-6 h-6 text-rose-400" />
            <span className="text-lg font-semibold text-rose-400">Triggered Alerts</span>
          </div>
          <p className="mt-2 text-3xl font-bold text-[var(--text-primary)]">{triggeredDevices.length}</p>
        </div>
      </div>

    {/* 4 Columns for Pie and Line charts*/}
    <div className="flex-1 grid grid-cols-4 gap-6 h-full">

      {/* Column 1: BLE Company Pie Chart */}
      <div className="flex flex-col h-full col-span-1 bg-[var(--surface-900)] p-6 rounded-xl shadow-sm border border-[var(--surface-800)] backdrop-blur-sm">
        <h3 className="text-l font-bold text-[var(--text-primary)] mb-4">Logs by BLE Company</h3>
        {/* Wrapper to center PieChart vertically */}
        <div className="flex flex-col flex-1 items-center justify-start">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                {companyData.map((_, index) => (
                  <linearGradient
                    key={`gradient-${index}`}
                    id={`gradient-${index}`}
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor={`hsl(${(index * 360) / companyData.length}, 70%, 55%)`} />
                    <stop offset="100%" stopColor={`hsl(${(index * 360) / companyData.length}, 85%, 45%)`} />
                  </linearGradient>
                ))}
              </defs>

              <Pie
                data={companyData}
                dataKey="count"
                nameKey="company"
                cx="50%"
                cy="45%"          // slightly above center to leave room for legend
                innerRadius={40}
                outerRadius={80}
                paddingAngle={5}
                cornerRadius={5}
              >
                {companyData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={`url(#gradient-${index})`} stroke="none" />
                ))}
              </Pie>

              <Tooltip content={(CustomTooltip)} />

              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{
                  maxHeight: '25%',  // limit legend height to prevent overflow
                  overflowY: 'auto', // scroll if too many items
                }}
                iconSize={10}
                iconType="circle"
                formatter={(value) => <span style={{ color: 'white', fontSize: '10px' }}>{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Column 2: MAC Vendor Pie Chart */}
      <div className="flex flex-col h-full col-span-1 bg-[var(--surface-900)] p-6 rounded-xl shadow-sm border border-[var(--surface-800)] backdrop-blur-sm">
          <h3 className="text-l font-bold text-[var(--text-primary)] mb-4">Logs by MAC Vendor</h3>
          {/* Wrapper to center PieChart vertically */}
          <div className="flex flex-col flex-1 items-center justify-start">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <defs>
                  {vendorData.map((_, index) => (
                    <linearGradient
                      key={`gradient-${index}`}
                      id={`gradient-${index}`}
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor={`hsl(${(index * 360) / vendorData.length}, 70%, 55%)`} />
                      <stop offset="100%" stopColor={`hsl(${(index * 360) / vendorData.length}, 85%, 45%)`} />
                    </linearGradient>
                  ))}
                </defs>

                <Pie
                  data={vendorData}
                  dataKey="count"
                  nameKey="vendor"
                  cx="50%"
                  cy="45%"          // slightly above center to leave room for legend
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  cornerRadius={5}
                >
                  {vendorData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={`url(#gradient-${index})`} stroke="none" />
                  ))}
                </Pie>

                <Tooltip />

                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{
                    maxHeight: '25%',  // limit legend height to prevent overflow
                    overflowY: 'auto', // scroll if too many items
                  }}
                  iconSize={10}
                  iconType="circle"
                  formatter={(value) => <span style={{ color: 'white', fontSize: '10px' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
      </div>

      {/* Column 3+4: Line Chart */}
      <div className="flex flex-col flex-1 bg-[var(--surface-900)] p-6 rounded-xl shadow-sm border border-[var(--surface-800)] backdrop-blur-sm col-span-2">
        <h3 className="text-l font-bold text-[var(--text-primary)] mb-6">Hourly Logs Trend</h3>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={hourlyLogsByProtocol} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
            <CartesianGrid stroke={color.surface800} strokeDasharray="5 5" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="BLE" stroke={color.dataBle} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="WIFI" stroke={color.dataWifi} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
    </div>

  </div>
  );

const renderProfiles = () => (
  <div className="flex flex-1 gap-6 h-full">
    <div className="flex-1 p-4 bg-[var(--surface-900)] rounded-xl border border-[var(--surface-800)] flex flex-col">
      <div className="flex items-center justify-between mb-4">
        {/* <span className="text-lg font-semibold text-white">Profiles</span> */}
         <button
            onClick={() => setShowCreateProfile(true)}
            className="flex items-center gap-2 text-[var(--signal-cyan)] border border-[var(--signal-cyan)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
            >
            <PlusCircleIcon className="w-5 h-5" />
            <span>Create Profile</span>
          </button>
      </div>

      <div className="flex-1 overflow-y-auto space-y-2 max-h-full">
        {profiles.length > 0 ? (
          profiles.map(profile => {
            const matches = getProfileMatches(profile);
            const macCounts = matches.reduce((acc, match) => {
              if (match.address_type === 'public') {
                acc.public.add(match.mac_address);
              } else if (match.address_type === 'random') {
                acc.random.add(match.mac_address);
              }

              if (match.wifi_ssid) {
                acc.wifiSsids.add(match.wifi_ssid);
              }

              return acc;
            }, { public: new Set(), random: new Set(), wifiSsids: new Set() });

            const publicMacs = macCounts.public.size;
            const randomMacs = macCounts.random.size;
            const uniqueWifiSsids = macCounts.wifiSsids.size;
            
            return (
              <div key={profile.id} className="p-3 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg hover:border-[var(--surface-800)] transition-colors">
                {/* Header with name, stats, and actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className={`w-3 h-3 rounded-full border-2 transition-colors duration-200 ${
                        activeProfileId === profile.id
                          ? 'bg-[var(--signal-cyan)] border-[var(--signal-cyan)]'
                          : 'bg-transparent border-[var(--text-muted)]'
                      }`}
                    />
                    <span className="text-sm font-semibold text-[var(--text-primary)]">{profile.name}</span>
                    
                    {/* Inline stats */}
                    <div className="flex items-center space-x-3 text-xs text-[var(--text-muted)]">
                      <span>{publicMacs} Public MACs</span>
                      <span>{uniqueWifiSsids} WIFI SSIDs</span>
                      <span>{matches.length} Logs</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => setActiveProfileId(activeProfileId === profile.id ? null : profile.id)}
                      aria-label={activeProfileId === profile.id ? 'Hide profile details' : 'View profile details'}
                      title={activeProfileId === profile.id ? 'Hide profile details' : 'View profile details'}
                      className={`p-1 rounded transition-colors ${
                        activeProfileId === profile.id ? 'bg-[var(--signal-cyan)] text-[var(--ink-950)]' : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      <Eye size={12} />
                    </button>
                    <button
                      onClick={() => setProfiles(profiles.filter(p => p.id !== profile.id))}
                      aria-label="Delete profile"
                      title="Delete profile"
                      className="p-1 text-[var(--status-error)] hover:text-[var(--text-primary)] transition-colors rounded hover:bg-[var(--surface-800)]"
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>

                {/* Description - only if exists and profile is active */}
                {profile.description && activeProfileId === profile.id && (
                  <p className="text-xs text-[var(--text-muted)] mt-2">{profile.description}</p>
                )}

                {/* Active Filters - condensed */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {profile.rules.slice(0, 3).map((rule, index) => {
                    const fieldObj = availableFields.find(f => f.value === rule.field);
                    const operatorObj = operators.find(o => o.value === rule.operator);
                    
                    return (
                      <div key={index} className="text-xs bg-[var(--surface-900)] border border-[var(--surface-800)] rounded px-1.5 py-0.5">
                        <span className="text-[var(--signal-cyan)]">{fieldObj?.label}</span>
                        <span className="text-[var(--text-muted)]">{operatorObj?.symbol}</span>
                        {rule.value && <span className="text-[var(--text-primary)]">'{rule.value}'</span>}
                      </div>
                    );
                  })}
                  {profile.rules.length > 3 && (
                    <div className="text-xs text-[var(--text-muted)] px-1">+{profile.rules.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex-1 flex items-center justify-center text-center">
            <div>
              <Fingerprint size={32} className="mx-auto mb-2 opacity-30 text-[var(--text-muted)]" />
              <p className="text-sm text-[var(--text-primary)]">No profiles created</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">Click "Create Profile" to get started</p>
            </div>
          </div>
        )}
      </div>
    </div>

    <div className="flex-1 p-4 bg-[var(--surface-900)] rounded-xl border border-[var(--surface-800)] flex flex-col">
      {activeProfileId ? (
        // Show selected profile details
        (() => {
          const activeProfile = profiles.find(p => p.id === activeProfileId);
          const matches = getProfileMatches(activeProfile);
          const macCounts = matches.reduce((acc, match) => {
              if (match.address_type === 'public') {
                acc.public.add(match.mac_address);
              } else if (match.address_type === 'random') {
                acc.random.add(match.mac_address);
              }

              if (match.wifi_ssid) {
                acc.wifiSsids.add(match.wifi_ssid);
              }

              return acc;
            }, { public: new Set(), random: new Set(), wifiSsids: new Set() });

          const publicMacs = macCounts.public.size;
          const randomMacs = macCounts.random.size;
          const uniqueWifiSsids = macCounts.wifiSsids.size;
          const uniqueDevices = [...new Set(matches.map(m => m.mac_address))].length;
          const profileData = {
            packet_ids: [...new Set(matches.map(m => m.raw_packet_id).filter(Boolean))],
            public_mac_addresses: [...macCounts.public],
            random_mac_addresses: [...macCounts.random],
            device_names: [...new Set(matches.map(m => m.device_name).filter(Boolean))],
            wifi_ssids: [...new Set(matches.map(m => m.wifi_ssid).filter(Boolean))],
            mac_vendors: [...new Set(matches.map(m => m.mac_vendor).filter(Boolean))],
            ble_company_names: [...new Set(matches.map(m => m.ble_company_name).filter(Boolean))],
            ble_device_models: [...new Set(matches.map(m => m.ble_device_model).filter(Boolean))],
            ble_device_colors: [...new Set(matches.map(m => m.ble_device_color).filter(Boolean))]
          };

          const sortedMatches = matches.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          
          // Filter matches that have valid coordinates for map view
          const mapMatches = sortedMatches.filter(item => 
            item.latitude && 
            item.longitude && 
            !isNaN(parseFloat(item.latitude)) && 
            !isNaN(parseFloat(item.longitude))
          );

          const currentTrackingPoint = mapMatches[profileTimelineValue] || null;

          return (
                <>
                  {/* Header with toggle */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 rounded-full border-2 bg-[var(--signal-cyan)] border-[var(--signal-cyan)]"></div>
                      <span className="text-lg font-semibold text-[var(--text-primary)]">{activeProfile.name}</span>
                    </div>

                    <div className="flex items-center space-x-4">
                      {/* Table-style toggle */}
                      <div className="flex border-b border-[var(--surface-800)]">
                        <button
                          onClick={() => setViewMode("stats")}
                          className={`flex items-center space-x-1 px-3 py-1 text-sm border-b-2 ${
                            viewMode === "stats"
                              ? "border-[var(--signal-cyan)] text-[var(--signal-cyan)]"
                              : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                          }`}
                        >
                          <span>Stats</span>
                        </button>
                        <button
                          onClick={() => setViewMode("map")}
                          className={`flex items-center space-x-1 px-3 py-1 text-sm border-b-2 ${
                            viewMode === "map"
                              ? "border-[var(--signal-cyan)] text-[var(--signal-cyan)]"
                              : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                          }`}
                        >
                          <span>Map</span>
                        </button>
                      </div>

                      {/* Close button */}
                      <button
                        onClick={() => setActiveProfileId(null)}
                        aria-label="Close profile details"
                        className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Conditional Rendering */}
                  {viewMode === "map" ? (
                    <>
                      {/* Map */}
                      <div className="bg-[var(--ink-950)] rounded-lg h-96 relative overflow-hidden border-2 border-[var(--surface-800)]">
                        <RealMap
                          mapData={mapMatches}
                          currentTrackingPoint={currentTrackingPoint}
                          selectedDevice={activeProfile}
                          mapView={mapView}
                          timelineValue={profileTimelineValue}
                        />
                      </div>

                      {/* Timeline */}
                      {activeProfile && mapMatches.length > 0 && (
                        <div className="mt-4 space-y-3">

                          {/* Timeline header + scrubber */}
                          <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg px-4 py-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-[var(--text-muted)] uppercase tracking-widest">Timeline</span>
                              <span className="text-xs font-mono text-[var(--signal-cyan)]">
                                {profileTimelineValue + 1} <span className="text-[var(--text-muted)]">/ {mapMatches.length}</span>
                              </span>
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={Math.max(0, mapMatches.length - 1)}
                              value={profileTimelineValue}
                              onChange={(e) => setprofileTimelineValue(parseInt(e.target.value))}
                              className="w-full cursor-pointer"
                              style={{ accentColor: 'var(--signal-cyan)' }}
                            />
                            {currentTrackingPoint && (
                              <div className="flex justify-between mt-1">
                                <span className="text-xs text-[var(--text-muted)]">
                                  {new Date(mapMatches[0].timestamp).toLocaleTimeString()}
                                </span>
                                <span className="text-xs text-[var(--text-muted)]">
                                  {new Date(mapMatches[mapMatches.length - 1].timestamp).toLocaleTimeString()}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Detail strip — single non-wrapping row of labeled fields */}
                          {currentTrackingPoint && (
                            <div className="flex items-start gap-5 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg px-4 py-3 overflow-x-auto whitespace-nowrap">
                              <div className="flex flex-col flex-shrink-0">
                                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Device</span>
                                <span className="text-sm font-semibold text-[var(--text-primary)]">
                                  {currentTrackingPoint.device_name || currentTrackingPoint.mac_address || 'Unknown Device'}
                                </span>
                              </div>

                              <div className="flex flex-col flex-shrink-0">
                                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">MAC</span>
                                <span className="text-xs font-[var(--font-mono)] text-[var(--text-muted)]">{currentTrackingPoint.mac_address}</span>
                              </div>

                              <div className="flex flex-col flex-shrink-0">
                                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Protocol</span>
                                <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
                                  {currentTrackingPoint.protocol === 'BLE'
                                    ? <Bluetooth size={11} className="text-[var(--data-ble)]" />
                                    : <Wifi size={11} className="text-[var(--data-wifi)]" />}
                                  {currentTrackingPoint.protocol}
                                </span>
                              </div>

                              <div className="flex flex-col flex-shrink-0">
                                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">RSSI</span>
                                <span className="text-xs font-[var(--font-mono)]" style={{ color: rssiColor(currentTrackingPoint.rssi) }}>
                                  {currentTrackingPoint.rssi} dBm
                                </span>
                              </div>

                              <div className="flex flex-col flex-shrink-0">
                                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Sensor</span>
                                <span className="text-xs text-[var(--text-muted)]">{currentTrackingPoint.sensor_name || '—'}</span>
                              </div>

                              <div className="flex flex-col flex-shrink-0">
                                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Location</span>
                                <span className="text-xs font-[var(--font-mono)] text-[var(--text-muted)]">
                                  {parseFloat(currentTrackingPoint.latitude).toFixed(5)}, {parseFloat(currentTrackingPoint.longitude).toFixed(5)}
                                </span>
                              </div>

                              {currentTrackingPoint.protocol === 'WIFI' && currentTrackingPoint.wifi_ssid && (
                                <div className="flex flex-col flex-shrink-0">
                                  <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">SSID</span>
                                  <span className="text-xs text-[var(--data-wifi)]">{currentTrackingPoint.wifi_ssid}</span>
                                </div>
                              )}

                              <div className="flex flex-col flex-shrink-0">
                                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Alerts</span>
                                <div className="flex items-center gap-1">
                                  {(() => {
                                    // Totals for this MAC across its whole history, not just this
                                    // instant — so the count doesn't change as you scrub the timeline.
                                    const triggeredAlerts = triggeredDevices
                                      .filter(d => d.mac_address === currentTrackingPoint.mac_address)
                                      .map(d => d.alertName);
                                    const alertCounts = triggeredAlerts.reduce((acc, name) => {
                                      acc[name] = (acc[name] || 0) + 1;
                                      return acc;
                                    }, {});
                                    return triggeredAlerts.length > 0 ? (
                                      Object.entries(alertCounts).map(([name, count]) => (
                                        <span key={name} className="px-1.5 py-px bg-[color-mix(in_srgb,var(--status-error)_20%,transparent)] text-[var(--status-error)] border border-[var(--status-error)] rounded text-[11px] leading-4 flex-shrink-0">
                                          {name}{count > 1 ? ` (${count})` : ''}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="px-1.5 py-px bg-[color-mix(in_srgb,var(--status-success)_20%,transparent)] text-[var(--status-success)] border border-[var(--status-success)] rounded text-[11px] leading-4 flex-shrink-0">
                                        None
                                      </span>
                                    );
                                  })()}
                                </div>
                              </div>

                              <div className="flex flex-col flex-shrink-0 ml-auto">
                                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Detected</span>
                                <span className="text-xs text-[var(--text-muted)] flex items-center gap-1.5">
                                  <Clock size={11} />
                                  {new Date(currentTrackingPoint.timestamp).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Stats Section */}
                      <div className="flex-1 overflow-y-auto space-y-4">
                              {/* Summary Stats */}
                              <div className="grid grid-cols-5 gap-3">
                                <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3 text-center">
                                  <div className="text-xl font-bold text-[var(--text-primary)]">{publicMacs}</div>
                                  <div className="text-xs text-[var(--text-muted)]">Public MAC Addresses</div>
                                </div>
                                <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3 text-center">
                                  <div className="text-xl font-bold text-[var(--text-primary)]">{randomMacs}</div>
                                  <div className="text-xs text-[var(--text-muted)]">Random MAC Addresses</div>
                                </div>
                                <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3 text-center">
                                  <div className="text-xl font-bold text-[var(--data-wifi)]">{uniqueWifiSsids}</div>
                                  <div className="text-xs text-[var(--text-muted)]">WIFI SSIDs</div>
                                </div>
                                <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3 text-center">
                                  <div className="text-xl font-bold text-[var(--text-primary)]">{matches.length}</div>
                                  <div className="text-xs text-[var(--text-muted)]">Logs</div>
                                </div>
                                <CompletenessIndicator matches={matches} />
                              </div>

                              {/* Description */}
                              {activeProfile.description && (
                                <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3">
                                  <h6 className="text-sm font-medium text-[var(--text-primary)] mb-2">Description</h6>
                                  <p className="text-xs text-[var(--text-muted)]">{activeProfile.description}</p>
                                </div>
                              )}

                              {/* Active Filters */}
                              <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3">
                                <h6 className="text-sm font-medium text-[var(--text-primary)] mb-2">Active Filters</h6>
                                <div className="space-y-2">
                                  {activeProfile.rules.map((rule, index) => {
                                    const fieldObj = availableFields.find(f => f.value === rule.field);
                                    const operatorObj = operators.find(o => o.value === rule.operator);
                                    
                                    return (
                                      <div key={index} className="flex items-center space-x-2 text-xs bg-[var(--surface-900)] border border-[var(--surface-800)] rounded px-2 py-1">
                                        <span className="text-[var(--signal-cyan)]">{fieldObj?.label}</span>
                                        <span className="text-[var(--text-muted)]">{operatorObj?.symbol}</span>
                                        {rule.value && <span className="text-[var(--text-primary)]">'{rule.value}'</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* MAC Addresses */}
                              <div className="grid grid-cols-2 gap-3">
                                {publicMacs > 0 && (
                                  <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3">
                                    <h6 className="text-sm font-medium text-[var(--text-primary)] mb-2 flex items-center space-x-2">
                                      <Target size={14} />
                                      <span>Public MAC Addresses ({publicMacs})</span>
                                    </h6>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {profileData.public_mac_addresses.map((mac, idx) => (
                                        <div key={idx} className="text-xs font-mono text-[var(--text-muted)] bg-[var(--surface-900)] rounded px-2 py-1">
                                          {mac}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {randomMacs > 0 && (
                                  <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3">
                                    <h6 className="text-sm font-medium text-[var(--text-primary)] mb-2 flex items-center space-x-2">
                                      <Target size={14} />
                                      <span>Random MAC Addresses ({randomMacs})</span>
                                    </h6>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {profileData.random_mac_addresses.map((mac, idx) => (
                                        <div key={idx} className="text-xs font-mono text-[var(--text-muted)] bg-[var(--surface-900)] rounded px-2 py-1">
                                          {mac}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* WIFI SSIDs */}
                              {profileData.wifi_ssids.length > 0 && (
                                <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3">
                                  <h6 className="text-sm font-medium text-[var(--text-primary)] mb-2 flex items-center space-x-2">
                                    <Wifi size={14} />
                                    <span>WIFI Networks ({profileData.wifi_ssids.length})</span>
                                  </h6>
                                  <div className="flex flex-wrap gap-1">
                                    {profileData.wifi_ssids.map((ssid, idx) => (
                                      <span key={idx} className="text-xs bg-[var(--surface-900)] text-[var(--data-wifi)] rounded px-2 py-1">
                                        {ssid}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* BLE Device Names */}
                              {profileData.device_names.length > 0 && (
                                <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3">
                                  <h6 className="text-sm font-medium text-[var(--text-primary)] mb-2 flex items-center space-x-2">
                                    <Bluetooth size={14} />
                                    <span>BLE Device Names ({profileData.device_names.length})</span>
                                  </h6>
                                  <div className="space-y-1">
                                    {profileData.device_names.map((name, idx) => (
                                      <div key={idx} className="text-xs text-[var(--text-muted)] bg-[var(--surface-900)] rounded px-2 py-1">
                                        {name}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {profileData.ble_device_models.length > 0 && (
                                <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3">
                                  <h6 className="text-sm font-medium text-[var(--text-primary)] mb-2">BLE Device Models ({profileData.ble_device_models.length})</h6>
                                  <div className="space-y-1">
                                    {profileData.ble_device_models.map((model, idx) => (
                                      <div key={idx} className="text-xs text-[var(--text-muted)] bg-[var(--surface-900)] rounded px-2 py-1">
                                        {model}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {profileData.ble_device_colors.length > 0 && (
                                <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3">
                                  <h6 className="text-sm font-medium text-[var(--text-primary)] mb-2">BLE Device Colors ({profileData.ble_device_colors.length})</h6>
                                  <div className="space-y-1">
                                    {profileData.ble_device_colors.map((color, idx) => (
                                      <div key={idx} className="text-xs text-[var(--text-muted)] bg-[var(--surface-900)] rounded px-2 py-1">
                                        {color}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* BLE Companies */}
                              {profileData.ble_company_names.length > 0 && (
                                <div className="bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg p-3">
                                  <h6 className="text-sm font-medium text-[var(--text-primary)] mb-2">BLE Companies ({profileData.ble_company_names.length})</h6>
                                  <div className="space-y-1">
                                    {profileData.ble_company_names.map((company, idx) => (
                                      <div key={idx} className="text-xs text-[var(--text-muted)] bg-[var(--surface-900)] rounded px-2 py-1">
                                        {company}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                    </>
                  )}
                </>
              );
        })()
      ) : (
        // Show default message when no profile selected
        <div className="flex-1 flex items-center justify-center text-center">
          <div>
            <Eye size={48} className="mx-auto mb-4 opacity-30 text-[var(--text-muted)]" />
            <p className="text-lg text-[var(--text-primary)] mb-2">Select a profile to view details</p>
            <p className="text-sm text-[var(--text-muted)]">Click the eye icon on any profile from the left panel to view detailed analysis and extracted data.</p>
          </div>
        </div>
      )}
    </div>

    <CreateProfileModal />
  </div>
);

const renderDeviceData = () => (
  <div className="flex flex-col flex-1 min-h-0">
    {/* Protocol switch — the table below only ever shows one protocol at a
        time, and which one is always explicit here (icon, color, count). */}
    <div className="flex items-center gap-2 mb-4 flex-shrink-0">
      <button
        onClick={() => setLogsView("wifi")}
        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
          logsView === "wifi"
            ? "bg-[color-mix(in_srgb,var(--data-wifi)_18%,transparent)] border-[var(--data-wifi)] text-[var(--data-wifi)]"
            : "border-[var(--surface-800)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        }`}
      >
        <Wifi className="w-4 h-4" />
        WiFi Logs
        <span className="text-xs opacity-75">({sortedWifiData.length})</span>
      </button>
      <button
        onClick={() => setLogsView("ble")}
        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
          logsView === "ble"
            ? "bg-[color-mix(in_srgb,var(--data-ble)_18%,transparent)] border-[var(--data-ble)] text-[var(--data-ble)]"
            : "border-[var(--surface-800)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        }`}
      >
        <Bluetooth className="w-4 h-4" />
        Bluetooth Logs
        <span className="text-xs opacity-75">({sortedBleData.length})</span>
      </button>
    </div>

    {/* Table — fills all remaining height as one scroll region */}
    <div className="flex-1 min-h-0 overflow-auto bg-[var(--surface-900)] rounded-xl">
      {logsView === "wifi" ? (
        <table className="min-w-full text-sm text-[var(--text-primary)]">
          <thead className="bg-[var(--surface-800)] text-[var(--text-muted)] sticky top-0 z-10">
            <tr>
              {[
                { label: "Time", key: "timestamp" },
                { label: "Sensor Name", key: "sensor_name" },
                { label: "SSID", key: "wifi_ssid" },
                { label: "MAC Address", key: "mac_address" },
                { label: "MAC Vendor", key: "mac_vendor" },
                { label: "RSSI", key: "rssi" },
                { label: "Location", key: "latitude" },
              ].map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleWifiSort(col.key)}
                  className="px-4 py-2 text-left cursor-pointer select-none group hover:text-[var(--text-primary)] transition-colors"
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    <ChevronDown
                      size={12}
                      className={`transition-transform ${
                        wifiSortKey === col.key
                          ? `text-[var(--signal-cyan)] ${wifiSortAsc ? "rotate-180" : ""}`
                          : "opacity-40 group-hover:opacity-70"
                      }`}
                    />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedWifiData.length > 0 ? (
              sortedWifiData.map((device, idx) => (
                <tr key={`wifi-${device.mac_address}-${device.timestamp}-${idx}`} className="border-b border-[var(--surface-800)] odd:bg-[var(--ink-950)]/40 hover:bg-[var(--surface-800)]">
                  <td className="px-4 py-2 font-[var(--font-mono)] whitespace-nowrap">{new Date(device.timestamp).toLocaleTimeString()}</td>
                  <td className="px-4 py-2 max-w-[160px] truncate" title={device.sensor_name}>{device.sensor_name}</td>
                  <td className="px-4 py-2 max-w-[200px] truncate" title={device.wifi_ssid}>{device.wifi_ssid}</td>
                  <td className="px-4 py-2 font-[var(--font-mono)] whitespace-nowrap">{device.mac_address}</td>
                  <td className="px-4 py-2 max-w-[160px] truncate" title={device.mac_vendor}>{device.mac_vendor}</td>
                  <td className="px-4 py-2 font-[var(--font-mono)] whitespace-nowrap" style={{ color: rssiColor(device.rssi) }}>{device.rssi} dBm</td>
                  <td className="px-4 py-2 font-[var(--font-mono)] whitespace-nowrap">{device.latitude}, {device.longitude}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center py-6 text-[var(--text-muted)]">
                  No WiFi logs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      ) : (
        <table className="min-w-full text-sm text-[var(--text-primary)]">
          <thead className="bg-[var(--surface-800)] text-[var(--text-muted)] sticky top-0 z-10">
            <tr>
              {[
                { label: "Time", key: "timestamp" },
                { label: "Sensor Name", key: "sensor_name" },
                { label: "Device Name", key: "device_name" },
                { label: "MAC Address", key: "mac_address" },
                { label: "MAC Vendor", key: "mac_vendor" },
                { label: "BLE Company", key: "ble_company_name" },
                { label: "RSSI", key: "rssi" },
                { label: "Location", key: "latitude" },
              ].map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleBleSort(col.key)}
                  className="px-4 py-2 text-left cursor-pointer select-none group hover:text-[var(--text-primary)] transition-colors"
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    <ChevronDown
                      size={12}
                      className={`transition-transform ${
                        bleSortKey === col.key
                          ? `text-[var(--signal-cyan)] ${bleSortAsc ? "rotate-180" : ""}`
                          : "opacity-40 group-hover:opacity-70"
                      }`}
                    />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedBleData.length > 0 ? (
              sortedBleData.map((device, idx) => (
                <tr key={`ble-${device.mac_address}-${device.timestamp}-${idx}`} className="border-b border-[var(--surface-800)] odd:bg-[var(--ink-950)]/40 hover:bg-[var(--surface-800)]">
                  <td className="px-4 py-2 font-[var(--font-mono)] whitespace-nowrap">{new Date(device.timestamp).toLocaleTimeString()}</td>
                  <td className="px-4 py-2 max-w-[160px] truncate" title={device.sensor_name}>{device.sensor_name}</td>
                  <td className="px-4 py-2 max-w-[160px] truncate" title={device.device_name}>{device.device_name}</td>
                  <td className="px-4 py-2 font-[var(--font-mono)] whitespace-nowrap">{device.mac_address}</td>
                  <td className="px-4 py-2 max-w-[160px] truncate" title={device.mac_vendor}>{device.mac_vendor}</td>
                  <td className="px-4 py-2 max-w-[160px] truncate" title={device.ble_company_name}>{device.ble_company_name}</td>
                  <td className="px-4 py-2 font-[var(--font-mono)] whitespace-nowrap" style={{ color: rssiColor(device.rssi) }}>{device.rssi} dBm</td>
                  <td className="px-4 py-2 font-[var(--font-mono)] whitespace-nowrap">{device.latitude}, {device.longitude}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-6 text-[var(--text-muted)]">
                  No Bluetooth LE logs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  </div>
);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDeviceDropdown && !event.target.closest('.dropdown-container')) {
        setShowDeviceDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDeviceDropdown]);

  const [circleFilter, setCircleFilter] = useState(null);   // { center: LatLng, radius: meters } | null
  const [drawMode, setDrawMode] = useState(false);

  const renderMap = () => {
    // ── base geo-points for the current selection ──────────────────────
    const baseMapData = (selectedDevice ? currentDeviceData : missionData).filter(item =>
      item.latitude &&
      item.longitude &&
      !isNaN(parseFloat(item.latitude)) &&
      !isNaN(parseFloat(item.longitude))
    );

    // ── apply circle spatial filter if active ──────────────────────────
    const mapData = circleFilter
      ? baseMapData.filter(item => {
          const pt = window.L?.latLng
            ? window.L.latLng(parseFloat(item.latitude), parseFloat(item.longitude))
            : null;
          if (!pt) return true; // L not loaded yet, show all
          return circleFilter.center.distanceTo(pt) <= circleFilter.radius;
        })
      : baseMapData;

    // ── MAC list: when circle active, restrict to MACs that have at
    //    least one point inside the circle ─────────────────────────────
    const macsInCircle = circleFilter
      ? new Set(mapData.map(d => d.mac_address).filter(Boolean))
      : null;

    const filteredMacList = uniqueDevices.filter(mac => {
      const matchesSearch = mac.toLowerCase().includes(macSearch.toLowerCase());
      const inCircle = !macsInCircle || macsInCircle.has(mac);
      return matchesSearch && inCircle;
    });

    // Per-MAC metadata for the sidebar
    const getMacMeta = (mac) => {
      const logs = [...filteredWifiData, ...filteredBleData].filter(d => d.mac_address === mac);
      const protocols = [...new Set(logs.map(d => d.protocol).filter(Boolean))];
      const hasAlerts = triggeredDevices.some(d => d.mac_address === mac);
      const vendor = logs.find(d => d.mac_vendor)?.mac_vendor || null;
      const deviceName = logs.find(d => d.device_name)?.device_name || null;
      return { protocols, hasAlerts, vendor, deviceName, count: logs.length };
    };

    return (
      <div className="flex gap-4" style={{ minHeight: 0 }}>

        {/* ── Left sidebar: MAC list ── */}
        <div ref={macListPanelRef} className="flex flex-col w-72 flex-shrink-0 bg-[var(--surface-900)] border border-[var(--surface-800)] rounded-xl overflow-hidden">

          {/* Header */}
          <div className="px-4 pt-4 pb-3 border-b border-[var(--surface-800)]">
            {/* <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-3">Map View</div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-[var(--text-muted)]" />
              <input
                className="w-full pl-9 pr-3 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--signal-cyan)] transition-colors"
                placeholder="Search MAC address..."
                value={macSearch}
                onChange={e => setMacSearch(e.target.value)}
              />
            </div> */}
            <div className="mt-2 text-xs text-[var(--text-muted)]">
              {filteredMacList.length} of {uniqueDevices.length} MAC addresses
            </div>
          </div>

          {/* "All devices" row */}
          <div
            onClick={() => { setSelectedDevice(''); setTimelineValue(0); }}
            className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-[var(--surface-800)] transition-colors ${
              !selectedDevice
                ? 'bg-[var(--surface-800)] border-l-2 border-l-[var(--signal-cyan)]'
                : 'hover:bg-[var(--surface-800)] border-l-2 border-l-transparent'
            }`}
          >
            <div className="w-7 h-7 rounded-full bg-[var(--surface-800)] flex items-center justify-center flex-shrink-0">
              <MapPin size={13} className="text-[var(--text-muted)]" />
            </div>
            <div className="min-w-0">
              <div className={`text-xs font-medium truncate ${!selectedDevice ? 'text-[var(--signal-cyan)]' : 'text-[var(--text-muted)]'}`}>
                All Devices
              </div>
              <div className="text-xs text-[var(--text-muted)]">Packet count heatmap</div>
            </div>
          </div>

          {/* Scrollable MAC list */}
          <div className="flex-1 overflow-y-auto">
            {filteredMacList.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-[var(--text-muted)]">
                <Search size={20} className="mb-2 opacity-40" />
                <span className="text-xs">No matches found</span>
              </div>
            ) : (
              filteredMacList.map(mac => {
                const meta    = getMacMeta(mac);
                const isActive = selectedDevice === mac;
                return (
                  <div
                    key={mac}
                    onClick={() => { setSelectedDevice(mac); setTimelineValue(0); }}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer border-b border-[var(--ink-950)] transition-colors ${
                      isActive
                        ? 'bg-[var(--surface-800)] border-l-2 border-l-[var(--signal-cyan)]'
                        : 'hover:bg-[var(--surface-800)] border-l-2 border-l-transparent'
                    }`}
                  >
                    {/* Protocol icon */}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      meta.protocols.includes('BLE') && meta.protocols.includes('WIFI')
                        ? 'bg-[color-mix(in_srgb,var(--data-unknown)_25%,transparent)]'
                        : meta.protocols.includes('BLE')
                        ? 'bg-[color-mix(in_srgb,var(--data-ble)_25%,transparent)]'
                        : 'bg-[color-mix(in_srgb,var(--data-wifi)_25%,transparent)]'
                    }`}>
                      {meta.protocols.includes('BLE') && !meta.protocols.includes('WIFI')
                        ? <Bluetooth size={12} className="text-[var(--data-ble)]" />
                        : meta.protocols.includes('WIFI') && !meta.protocols.includes('BLE')
                        ? <Wifi size={12} className="text-[var(--data-wifi)]" />
                        : <Signal size={12} className="text-[var(--data-unknown)]" />}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 justify-between">
                        <span className={`text-xs font-mono truncate ${isActive ? 'text-[var(--signal-cyan)]' : 'text-[var(--text-primary)]'}`}>
                          {mac}
                        </span>
                        {meta.hasAlerts && (
                          <AlertTriangle size={11} className="text-orange-400 flex-shrink-0" />
                        )}
                      </div>
                      {(meta.deviceName || meta.vendor) && (
                        <div className="text-xs text-[var(--text-muted)] truncate mt-0.5">
                          {meta.deviceName || meta.vendor}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        {meta.protocols.map(p => (
                          <span key={p} className={`text-xs px-1.5 py-0.5 rounded ${
                            p === 'BLE'
                              ? 'bg-[color-mix(in_srgb,var(--data-ble)_20%,transparent)] text-[var(--data-ble)]'
                              : 'bg-[color-mix(in_srgb,var(--data-wifi)_20%,transparent)] text-[var(--data-wifi)]'
                          }`}>{p}</span>
                        ))}
                        <span className="text-xs text-[var(--text-muted)]">{meta.count} logs</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── Right panel: map + timeline + detail, framed like the Target Profiles
             map panel — one padded card with nested rounded modules, capped to the
             left panel's height. The map absorbs all flexible space so the rows
             below it are fixed-height and never need to scroll. ── */}
        <div
          className="flex-1 flex flex-col bg-[var(--surface-900)] border border-[var(--surface-800)] rounded-xl overflow-hidden p-4 min-w-0"
          style={mapColumnHeight ? { height: `${mapColumnHeight}px` } : undefined}
        >

        {/* ── Map toolbar (acts as this panel's header row) ── */}
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setDrawMode(d => !d);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${
                  drawMode
                    ? 'bg-[var(--signal-cyan)] border-[var(--signal-cyan)] text-[var(--ink-950)] shadow-[0_0_0_3px_rgba(63,193,203,0.2)]'
                    : 'bg-[var(--surface-800)] border-transparent text-[var(--text-muted)] hover:text-[var(--signal-cyan)]'
                }`}
              >
                {/* circle crosshair icon */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="8"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/>
                  <line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/>
                </svg>
                {drawMode ? 'Click & drag to draw…' : 'Select area'}
              </button>

              {circleFilter && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--signal-cyan)] bg-[var(--surface-800)] border border-[var(--signal-cyan)] rounded-md px-2 py-1 font-mono">
                    ⌀ {circleFilter.radius >= 1000
                      ? `${(circleFilter.radius / 1000).toFixed(1)} km`
                      : `${Math.round(circleFilter.radius)} m`}
                    {' · '}{mapData.length} pts · {macsInCircle?.size ?? '—'} MACs
                  </span>
                  <button
                    onClick={() => { setCircleFilter(null); setDrawMode(false); }}
                    className="text-xs text-[var(--text-muted)] hover:text-[var(--status-error)] transition-colors flex items-center gap-1"
                  >
                    <X size={12} /> Clear
                  </button>
                </div>
              )}
            </div>

            {drawMode && !circleFilter && (
              <span className="text-xs text-[var(--text-muted)] italic">Drag on the map to define a circle</span>
            )}
          </div>

          {/* Map — its own module, like the Target Profiles map card. Fills
              whatever space is left, so the rows below never need to scroll. */}
          <div className="flex-1 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg overflow-hidden" style={{ minHeight: '260px' }}>
            <RealMap
              mapData={mapData}
              currentTrackingPoint={currentTrackingPoint}
              selectedDevice={selectedDevice}
              mapView={mapView}
              timelineValue={timelineValue}
              drawMode={drawMode}
              circleFilter={circleFilter}
              onCircleDrawn={(cf) => { setCircleFilter(cf); setDrawMode(false); }}
              onCircleClear={() => { setCircleFilter(null); }}
              columnHeight={mapColumnHeight}
            />
          </div>

          {/* Timeline — one compact row, only when a device is selected */}
          {selectedDevice && currentDeviceData.length > 0 && (
            <div className="mt-4 flex items-center gap-3 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg px-4 py-3 flex-shrink-0">
              <span className="text-xs uppercase tracking-widest text-[var(--text-muted)] flex-shrink-0">Timeline</span>
              <span className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] flex-shrink-0">
                {new Date(currentDeviceData[0].timestamp).toLocaleTimeString()}
              </span>
              <input
                type="range"
                min={0}
                max={Math.max(0, currentDeviceData.length - 1)}
                value={timelineValue}
                onChange={e => setTimelineValue(parseInt(e.target.value))}
                className="w-full cursor-pointer flex-1"
                style={{ accentColor: 'var(--signal-cyan)' }}
              />
              <span className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] flex-shrink-0">
                {new Date(currentDeviceData[currentDeviceData.length - 1].timestamp).toLocaleTimeString()}
              </span>
              <span className="text-xs font-[var(--font-mono)] text-[var(--signal-cyan)] flex-shrink-0">
                {timelineValue + 1}/{currentDeviceData.length}
              </span>
            </div>
          )}

          {/* Detail — a single non-wrapping row of labeled fields; scrolls
              horizontally only in the rare case it doesn't fit, never vertically. */}
          {selectedDevice && currentTrackingPoint && (
            <div className="mt-4 flex items-start gap-5 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg px-4 py-3 flex-shrink-0 overflow-x-auto whitespace-nowrap">
              <div className="flex flex-col flex-shrink-0">
                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Device</span>
                <span className="text-sm font-semibold text-[var(--text-primary)]">
                  {currentTrackingPoint.device_name || currentTrackingPoint.mac_address || 'Unknown Device'}
                </span>
              </div>

              <div className="flex flex-col flex-shrink-0">
                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">MAC</span>
                <span className="text-xs font-[var(--font-mono)] text-[var(--text-muted)]">{currentTrackingPoint.mac_address}</span>
              </div>

              <div className="flex flex-col flex-shrink-0">
                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Protocol</span>
                <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
                  {currentTrackingPoint.protocol === 'BLE'
                    ? <Bluetooth size={11} className="text-[var(--data-ble)]" />
                    : <Wifi size={11} className="text-[var(--data-wifi)]" />}
                  {currentTrackingPoint.protocol}
                </span>
              </div>

              <div className="flex flex-col flex-shrink-0">
                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">RSSI</span>
                <span className="text-xs font-[var(--font-mono)]" style={{ color: rssiColor(currentTrackingPoint.rssi) }}>
                  {currentTrackingPoint.rssi} dBm
                </span>
              </div>

              <div className="flex flex-col flex-shrink-0">
                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Sensor</span>
                <span className="text-xs text-[var(--text-muted)]">{currentTrackingPoint.sensor_name || '—'}</span>
              </div>

              <div className="flex flex-col flex-shrink-0">
                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Location</span>
                <span className="text-xs font-[var(--font-mono)] text-[var(--text-muted)]">
                  {parseFloat(currentTrackingPoint.latitude).toFixed(5)}, {parseFloat(currentTrackingPoint.longitude).toFixed(5)}
                </span>
              </div>

              {currentTrackingPoint.mac_vendor && (
                <div className="flex flex-col flex-shrink-0">
                  <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Vendor</span>
                  <span className="text-xs text-[var(--text-muted)]">{currentTrackingPoint.mac_vendor}</span>
                </div>
              )}

              {currentTrackingPoint.protocol === 'WIFI' && currentTrackingPoint.wifi_ssid && (
                <div className="flex flex-col flex-shrink-0">
                  <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">SSID</span>
                  <span className="text-xs text-[var(--data-wifi)]">{currentTrackingPoint.wifi_ssid}</span>
                </div>
              )}

              <div className="flex flex-col flex-shrink-0">
                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Alerts</span>
                <div className="flex items-center gap-1">
                  {(() => {
                    // Totals for this MAC across its whole history, not just this
                    // instant — so the count doesn't change as you scrub the timeline.
                    const triggeredAlerts = triggeredDevices
                      .filter(d => d.mac_address === selectedDevice)
                      .map(d => d.alertName);
                    const alertCounts = triggeredAlerts.reduce((acc, name) => {
                      acc[name] = (acc[name] || 0) + 1;
                      return acc;
                    }, {});
                    return triggeredAlerts.length > 0 ? (
                      Object.entries(alertCounts).map(([name, count]) => (
                        <span key={name} className="px-1.5 py-px bg-[color-mix(in_srgb,var(--status-error)_20%,transparent)] text-[var(--status-error)] border border-[var(--status-error)] rounded text-[11px] leading-4 flex-shrink-0">
                          {name}{count > 1 ? ` (${count})` : ''}
                        </span>
                      ))
                    ) : (
                      <span className="px-1.5 py-px bg-[color-mix(in_srgb,var(--status-success)_20%,transparent)] text-[var(--status-success)] border border-[var(--status-success)] rounded text-[11px] leading-4 flex-shrink-0">
                        None
                      </span>
                    );
                  })()}
                </div>
              </div>

              <div className="flex flex-col flex-shrink-0 ml-auto">
                <span className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Detected</span>
                <span className="text-xs text-[var(--text-muted)] flex items-center gap-1.5">
                  <Clock size={11} />
                  {new Date(currentTrackingPoint.timestamp).toLocaleString()}
                </span>
              </div>
            </div>
          )}

          {/* Empty state — one compact row, only when no device is selected */}
          {!selectedDevice && (
            <div className="mt-4 flex items-center gap-3 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-lg px-4 py-3 flex-shrink-0">
              <MapPin size={14} className="text-[var(--text-muted)] flex-shrink-0" />
              <span className="text-xs text-[var(--text-muted)]">
                Select a device from the list on the left to see the sensors location when the device was seen and detection details.
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAlerts = () => (
    <div className="space-y-6">
      <div className="bg-[var(--surface-900)] rounded-lg border border-[var(--surface-800)] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">Rule Configuration</h3>
          <button
            onClick={() => setShowNewAlertModal(true)}
            className="flex items-center gap-2 text-[var(--signal-cyan)] border border-[var(--signal-cyan)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
            >
            <PlusCircleIcon className="w-5 h-5" />
            <span>Create Rule</span>
          </button>
        </div>

        <div className="space-y-3">
          {alerts.map((alert) => (
            <div key={alert.id} className="flex items-center justify-between p-4 border border-[var(--surface-800)] rounded-lg">
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  checked={alert.active}
                  onChange={() => toggleAlert(alert.id)}
                  className="h-4 w-4"
                  style={{ accentColor: 'var(--signal-cyan)' }}
                />
                <div>
                  <p className="font-medium text-[var(--text-primary)]">{alert.name}</p>
                  <p className="text-sm text-[var(--text-muted)]">
                    {alert.field} {alert.operator.replace('_', ' ')} "{alert.value}"
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-[var(--text-muted)]">
                  {triggeredDevices.filter(d => d.alertId === alert.id).length} triggered
                </span>
                <button
                  onClick={() => removeAlert(alert.id)}
                  aria-label="Delete rule"
                  title="Delete rule"
                  className="text-[var(--status-error)] hover:text-[var(--text-primary)]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Triggered Alerts */}
      <div className="bg-[var(--surface-900)] rounded-lg border border-[var(--surface-800)]">
        <div className="p-4 border-b border-[var(--surface-800)]">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">Triggered Alerts ({triggeredDevices.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-[var(--text-primary)]">
            <thead className="bg-[var(--surface-800)] text-[var(--text-muted)]">
              <tr>
                <th className="px-4 py-2 text-left">Alert</th>
                <th className="px-4 py-2 text-left">Time</th>
                <th className="px-4 py-2 text-left">Sensor Name</th>
                <th className="px-4 py-2 text-left">Device Name</th>
                <th className="px-4 py-2 text-left">MAC Address</th>
                <th className="px-4 py-2 text-left">Protocol</th>
                <th className="px-4 py-2 text-left">RSSI</th>
              </tr>
            </thead>
            <tbody>
              {triggeredDevices.length > 0 ? (
                triggeredDevices.map((device, idx) => (
                  <tr key={idx} className="border-b border-[var(--surface-800)] odd:bg-[var(--ink-950)]/40 hover:bg-[var(--surface-800)]">
                    <td className="px-4 py-2">
                      <span className="px-2 py-0.5 text-xs bg-[color-mix(in_srgb,var(--status-error)_20%,transparent)] text-[var(--status-error)] border border-[var(--status-error)] rounded-full">
                        {device.alertName}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-[var(--font-mono)] whitespace-nowrap">
                      {new Date(device.timestamp).toLocaleTimeString()}
                    </td>
                    <td className="px-4 py-2">{device.sensor_name}</td>
                    <td className="px-4 py-2 font-medium">
                      {device.protocol === 'WIFI' ? device.wifi_ssid : device.device_name}
                    </td>
                    <td className="px-4 py-2 font-[var(--font-mono)] whitespace-nowrap">{device.mac_address}</td>
                    <td className="px-4 py-2">{device.protocol}</td>
                    <td className="px-4 py-2 font-[var(--font-mono)] whitespace-nowrap" style={{ color: rssiColor(device.rssi) }}>{device.rssi} dBm</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-6 text-[var(--text-muted)]">
                    No alerts triggered yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Rule Modal */}
      {showNewAlertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-900)] border border-[var(--surface-800)] rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Create New Rule</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-muted)]">Rule Name</label>
                <input
                  type="text"
                  value={newAlert.name}
                  onChange={(e) => setNewAlert({...newAlert, name: e.target.value})}
                  className="mt-1 w-full px-3 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md text-[var(--text-primary)]"
                  placeholder="Enter rule name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-muted)]">Field</label>
                <div className="relative mt-1">
                  <select
                    value={newAlert.field}
                    onChange={(e) => setNewAlert({...newAlert, field: e.target.value})}
                    className="w-full appearance-none pl-3 pr-9 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--signal-cyan)] focus:border-[var(--signal-cyan)] transition-colors"
                  >
                    <option value="">Select field</option>
                    <option value="device_name">Device Name</option>
                    <option value="mac_address">MAC Address</option>
                    <option value="mac_vendor">MAC Vendor</option>
                    <option value="ble_company_name">BLE Company</option>
                    <option value="manufacturer_data">Manufacturer Data</option>
                    <option value="rssi">RSSI</option>
                    <option value="protocol">Protocol</option>
                    <option value="wifi_ssid">WIFI SSID</option>
                    <option value="address_type">Address Type</option>
                  </select>
                  <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-muted)]">Operator</label>
                <div className="relative mt-1">
                  <select
                    value={newAlert.operator}
                    onChange={(e) => setNewAlert({...newAlert, operator: e.target.value})}
                    className="w-full appearance-none pl-3 pr-9 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--signal-cyan)] focus:border-[var(--signal-cyan)] transition-colors"
                  >
                    <option value="equals">Equals</option>
                    <option value="contains">Contains</option>
                    <option value="greater_than">Greater Than</option>
                    <option value="less_than">Less Than</option>
                  </select>
                  <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-muted)]">Value</label>
                <input
                  type="text"
                  value={newAlert.value}
                  onChange={(e) => setNewAlert({...newAlert, value: e.target.value})}
                  className="mt-1 w-full px-3 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md text-[var(--text-primary)]"
                  placeholder="Enter value to match"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="alertActive"
                  checked={newAlert.active}
                  onChange={(e) => setNewAlert({...newAlert, active: e.target.checked})}
                  className="h-4 w-4"
                  style={{ accentColor: 'var(--signal-cyan)' }}
                />
                <label htmlFor="alertActive" className="text-sm text-[var(--text-muted)]">Active</label>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                    onClick={() => setShowNewAlertModal(false)}
                    className="mt-4 text-[var(--text-muted)] border border-[var(--surface-800)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
                  >
                    Cancel
              </button>

              <button
                    onClick={addAlert}
                    className="mt-4 text-[var(--status-success)] border border-[var(--status-success)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
                  >
                    Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--ink-950)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--signal-cyan)] mx-auto"></div>
          <p className="mt-4 text-[var(--text-primary)]">Loading mission data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--ink-950)] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-[var(--status-error)] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Error Loading Mission</h2>
          <p className="text-[var(--text-muted)] mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-[var(--signal-cyan)] border border-[var(--signal-cyan)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[var(--ink-950)] overflow-hidden">
      {/* Header */}
      <div className="bg-[var(--ink-950)] text-[var(--text-primary)] px-6 py-4">
        <div className="flex items-center justify-between border-b border-[var(--surface-800)] pb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back</span>
            </button>
            <div>
              <h1 className="text-2xl font-bold tracking-wide">Mission Viewer</h1>
              <p className="text-sm text-[var(--text-muted)]">Viewing wireless data from mission sensors</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Info Bar */}
      {missionInfo && (
        <div className="bg-[var(--ink-950)] text-[var(--text-muted)] px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div>
                <span className="text-sm">Mission:</span>
                <span className="ml-2 text-sm text-[var(--text-primary)]">{missionInfo.title}</span>
              </div>
              <div>
                <span className="text-sm">Start Time:</span>
                <span className="ml-2 text-sm text-[var(--text-primary)]">
                  {new Date(missionInfo.start_time).toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-sm">End Time:</span>
                <span className="ml-2 text-sm text-[var(--text-primary)]">
                  {new Date(missionInfo.end_time).toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-sm">Total Logs:</span>
                <span className="ml-2 text-sm text-[var(--text-primary)]">{missionData.length}</span>
              </div>
               <div className="flex items-center">
                  <span className="text-sm">Tags:</span>
                  <div className="ml-2 flex flex-wrap gap-2">
                    {missionInfo.tags?.length ? (
                      missionInfo.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 text-xs rounded-full bg-[var(--surface-800)] text-[var(--text-primary)] font-medium"
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-[var(--text-muted)] font-medium">None</span>
                    )}
                  </div>
                </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="bg-[var(--ink-950)]">
        <div className="px-6">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: Eye },
              { id: 'map', name: 'Map View', icon: MapPin },
              { id: 'profiles', name: 'Target Profiles', icon: Fingerprint },
              { id: 'alerts', name: 'Rules & Alerts', icon: AlertTriangle },
              { id: 'devices', name: 'Log View', icon: Logs }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 font-medium text-sm border-b-2 ${
                    activeView === tab.id
                      ? 'border-[var(--signal-cyan)] text-[var(--signal-cyan)]'
                      : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-[var(--ink-950)] px-6 py-4">
        <div className="flex items-center space-x-4">
          {/* Free text search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-5 h-5 text-[var(--text-muted)]" />
              <input
                className="w-full pl-10 pr-4 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md text-[var(--text-muted)]"
                placeholder="Search devices, names, manufacturers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Dynamic key-value dropdowns */}
          <div className="flex items-center space-x-2">
            {/* Key tree dropdown */}
            <FieldTreeDropdown
              availableFields={availableFields}
              selectedValue={selectedKey}
              onChange={(value) => {
                setSelectedKey(value);
                setSelectedValue('');
              }}
            />

            {/* Value dropdown — styled to match the Key tree dropdown beside it */}
            <div className="relative" style={{ minWidth: 160 }}>
              <select
                value={selectedValue}
                onChange={(e) => {
                    const value = e.target.value;
                    setSelectedValue(value);
                    setActiveFilters({[selectedKey]: value });
                  }
                }
                disabled={!selectedKey}
                className="w-full appearance-none pl-3 pr-9 py-2 rounded-md text-sm transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  border: '1px solid',
                  borderColor: selectedValue ? 'var(--signal-cyan)' : 'var(--surface-800)',
                  color: selectedValue ? 'var(--text-primary)' : 'var(--text-muted)',
                  background: selectedValue ? 'var(--surface-800)' : 'var(--ink-950)',
                }}
              >
                <option value="">Select Value</option>
                {distinctValues.map((val) => (
                  <option key={val} value={val}>
                    {val}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: selectedValue ? 'var(--signal-cyan)' : 'var(--text-muted)' }}
              />
            </div>

            {/* Clear filters button */}
            {(selectedKey || selectedValue || searchQuery) && (
              <button
                onClick={() => {
                  setSelectedKey('');
                  setSelectedValue('');
                  setSearchQuery('');
                  setActiveFilters({});
                }}
                className="text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>


      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6 bg-[var(--ink-950)] text-[var(--text-muted)] flex flex-col gap-6">
        {activeView === 'overview' && renderOverview()}
        {activeView === 'devices' && renderDeviceData()}
        {activeView === 'alerts' && renderAlerts()}
        {activeView === 'profiles' && renderProfiles()}
        {activeView === 'map' && renderMap()}
      </div>
    </div>
  );
};

export default MissionView;